import React, { useEffect, useState, useCallback } from "react";
import { FieldArray, useFormikContext } from "formik";
import { Dialog } from "@headlessui/react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useDispatch } from "react-redux";
import { RolesListAction } from "../../redux/actions/general-action";
import { getUserByRoleIdAction } from "../../redux/actions/approval-work-flow-action";
import MultiSelectChips from "../../components/forms/multi-chip-select";
import { Info, GripVertical, Trash2, Pen } from "lucide-react";
import errorToast from "../../components/helper-components/error-toast";
import LoadingOverlay from "../../components/helper-components/overlay-loading";
import Select from "react-select"; // ✅ added

const TIMELINES = [
  { label: "Less than 3 days", value: "<3" },
  { label: "Within a week", value: "<7" },
  { label: "Within 10 days", value: "<10" },
];

const ApprovalStages = () => {
  const { values, setFieldValue } = useFormikContext();
  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [roleOptions, setRoleOptions] = useState([]);
  const [usersOptionsByRole, setUsersOptionsByRole] = useState({});
  const [rowLoading, setRowLoading] = useState({});

  useEffect(() => {
    setIsLoading(true);
    dispatch(
      RolesListAction(
        (data) => {
          setIsLoading(false);
          const rows = data?.data || [];
          const opts = rows.map((r) => ({
            value: r.role_id,
            label: r.role_name,
          }));
          setRoleOptions(opts);
        },
        (err) => {
          setIsLoading(false);
          setRoleOptions([]);
          errorToast(err?.message || "Failed to load roles");
        }
      )
    );
  }, [dispatch]);

  const fetchUsersByRole = useCallback(
    (roleId) =>
      new Promise((resolve) => {
        if (!roleId) return resolve([]);
        dispatch(
          getUserByRoleIdAction(
            { role_id: roleId },
            (data) => {
              const rows = data?.data || [];
              const opts = rows.map((u) => ({
                value: u.user_id,
                label: u.user_name,
              }));
              resolve(opts);
            },
            (err) => {
              errorToast(err?.message || "Failed to load users");
              resolve([]);
            }
          )
        );
      }),
    [dispatch]
  );

  useEffect(() => {
    (async () => {
      const stages = values.approvalStages || [];
      const roleIds = [...new Set(stages.map((s) => s.role))];
      for (const rid of roleIds) {
        if (!rid || usersOptionsByRole[rid]) continue;
        const opts = await fetchUsersByRole(rid);
        setUsersOptionsByRole((prev) => ({ ...prev, [rid]: opts }));
      }
    })();
  }, [values.approvalStages, fetchUsersByRole]);

  const [newStage, setNewStage] = useState({
    role: "",
    role_name: "",
    timeline: "",
    users: [],
    actions: { approve: true, reject: true, rollback: false, edit: false },
  });

  const handleAddOrUpdateStage = (push, replace) => {
    if (!newStage.role) {
      errorToast("Please select a role for this stage.");
      return;
    }

    if (!newStage.timeline) {
      errorToast("Please select an approval timeline.");
      return;
    }

    if (!newStage.users || newStage.users.length === 0) {
      errorToast("Please select at least one user for this stage.");
      return;
    }

    const hasAtLeastOneAction = Object.values(newStage.actions).some(Boolean);
    if (!hasAtLeastOneAction) {
      errorToast("At least one action must be enabled (approve, reject, etc.).");
      return;
    }

    const formattedStage = {
      ...newStage,
      role_name:
        newStage.role_name ||
        roleOptions.find((r) => r.value === newStage.role)?.label ||
        "",
      users: newStage.users.map((u) =>
        typeof u === "string" ? { value: u, label: u } : u
      ),
    };

    if (editIndex !== null) replace(editIndex, formattedStage);
    else push(formattedStage);

    handleCloseModal();
  };

  const handleEditStage = (stage, index) => {
    setNewStage(stage);
    setEditIndex(index);
    setIsOpen(true);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
    setEditIndex(null);
    setNewStage({
      role: "",
      role_name: "",
      timeline: "",
      users: [],
      actions: { approve: true, reject: true, rollback: false, edit: false },
    });
  };

  const getLabel = (options, value) => {
    if (typeof value === "object") return value.label;
    return options.find((opt) => opt.value === value)?.label || value;
  };

  const setRowLoad = (id, v) =>
    setRowLoading((prev) => ({ ...prev, [id]: !!v }));

  return (
    <FieldArray
      name="approvalStages"
      render={({ push, remove, replace }) => (
        <div className="w-full ">
          <LoadingOverlay isLoading={isLoading} />

          <div className="flex justify-end mb-3">
            <button
              type="button"
              className="bg-primary text-white px-3 py-2 rounded-md text-sm shadow-sm hover:opacity-95"
              onClick={() => {
                setIsOpen(true);
                setEditIndex(null);
                setNewStage({
                  role: "",
                  role_name: "",
                  timeline: "",
                  users: [],
                  actions: {
                    approve: true,
                    reject: true,
                    rollback: false,
                    edit: false,
                  },
                });
              }}
            >
              + Add Stage
            </button>
          </div>

          <DragDropContext
            onDragEnd={(result) => {
              if (!result.destination) return;
              const reordered = Array.from(values.approvalStages);
              const [moved] = reordered.splice(result.source.index, 1);
              reordered.splice(result.destination.index, 0, moved);
              setFieldValue("approvalStages", reordered);
            }}
          >
            <Droppable droppableId="stages">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {values.approvalStages?.map((stage, index) => (
                    <Draggable
                      key={index}
                      draggableId={`stage-${index}`}
                      index={index}
                    >
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className="border border-[var(--button-border-color)] p-4 mb-3 rounded-md shadow-sm bg-white select-none"
                        >
                          <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center gap-2">
                              <span
                                {...provided.dragHandleProps}
                                className="cursor-move text-gray-400"
                              >
                                <GripVertical size={16} />
                              </span>
                              <div className="font-semibold text-sml">
                                {stage.role_name ||
                                  getLabel(roleOptions, stage.role) ||
                                  "New Stage"}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => handleEditStage(stage, index)}
                                className="text-primary hover:text-white hover:bg-primary p-1 rounded-md border "
                              >
                                <Pen size={14} />
                              </button>
                              <button
                                type="button"
                                onClick={() => remove(index)}
                                className="text-[var(--remove-color)] hover:text-white hover:bg-[var(--remove-color)] p-1 rounded-md border"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="text-sm text-[var(--description-color)]">
                                Role
                              </label>
                              <input
                                value={
                                  stage.role_name ||
                                  getLabel(roleOptions, stage.role)
                                }
                                readOnly
                                className="border rounded-md w-full px-3 py-2 bg-gray-50 mt-2 border-[var(--button-border-color)] cursor-not-allowed"
                              />
                            </div>
                            <div>
                              <label className="text-sm text-[var(--description-color)]">
                                Approval Timeline
                              </label>
                              <input
                                value={getLabel(TIMELINES, stage.timeline)}
                                readOnly
                                className="border rounded-md w-full px-3 py-2 bg-gray-50 mt-2 border-[var(--button-border-color)] cursor-not-allowed"
                              />
                              <div className="pt-1 flex items-center gap-1">
                                <Info
                                  size={20}
                                  fill="var(--description-color)"
                                  stroke="white"
                                />
                                <span className="text-xs text-[var(--description-color)]">
                                  Define the maximum time allowed for this
                                  approval.
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="mt-3 border border-[var(--button-border-color)] p-4 rounded-md">
                            <label className="text-sm font-medium text-[var(--description-color)]">
                              Users
                            </label>
                            <div className="flex flex-wrap gap-2 mt-2">
                              {(stage.users || []).map((u, i) => (
                                <span
                                  key={i}
                                  className="bg-white border px-2 py-1 rounded-full text-sm border-[var(--button-border-color)] cursor-not-allowed"
                                >
                                  {getLabel(
                                    usersOptionsByRole[stage.role] || [],
                                    u
                                  )}
                                </span>
                              ))}
                              <span className="bg-white px-2 py-1 text-[var(--placeholder-color)]">
                                Add more users...
                              </span>
                            </div>
                          </div>

                          {/* <div className="mt-4 flex flex-wrap gap-4">
                            {Object.entries(stage.actions).map(([key, val]) => (
                              <label
                                key={key}
                                className="flex items-center gap-2"
                              >
                                <input
                                  type="checkbox"
                                  checked={val}
                                  readOnly
                                  className="cursor-not-allowed"
                                  style={{ accentColor: "var(--primary)" }}
                                />
                                <span className="text-sm capitalize">{key}</span>
                              </label>
                            ))}
                          </div> */}
                          <div className="mt-4">
                            <label className="text-sm font-bold text-[var(--description-color)]">
                              Actions
                            </label>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 mt-3">
                              {Object.entries(stage.actions).map(([key, val]) => {
                                const actionDescriptions = {
                                  approve: "Allow this stage to approve.",
                                  reject: "Permit rejection with remarks.",
                                  rollback: "Send the document back to previous stage.",
                                  edit: "Allow modification before approval.",
                                };

                                return (
                                  <div
                                    key={key}
                                    className="flex flex-col items-start gap-1"
                                  >
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        checked={val}
                                        readOnly
                                        className="h-4 w-4 cursor-not-allowed"
                                        style={{ accentColor: "var(--primary)" }}
                                      />
                                      <span className="text-sm font-medium capitalize">
                                        {key}
                                      </span>
                                    </div>
                                    <span className="text-xs text-[var(--description-color)] ml-6">
                                      {actionDescriptions[key]}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          {/* ✅ Modal */}
          <Dialog
            open={isOpen}
            onClose={handleCloseModal}
            className="relative z-[1001]"
          >
            <div className="fixed inset-0 bg-black opacity-50 backdrop-blur-sm transition-opacity duration-300" />
            <div className="fixed inset-0 flex items-center justify-center p-4">
              <Dialog.Panel className="bg-white rounded-md p-6 w-full max-w-lg shadow-2xl">
                <Dialog.Title className="text-lg font-semibold mb-4">
                  {editIndex !== null
                    ? "Edit Approval Stage"
                    : "Add Approval Stage"}
                </Dialog.Title>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-3">
                  <div>
                    <label className="text-sm font-bold">Role</label>
                    {/* ✅ React-Select instead of native select */}
                    <Select
                      className="mt-2"
                      classNamePrefix="role-select"
                      options={roleOptions}
                      placeholder="Select role"
                      isSearchable={true}
                      maxMenuHeight={5 * 40} // ~5 visible
                      components={{
                        IndicatorSeparator: () => null,
                      }}
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: "40px",
                          borderRadius: "0.375rem", // rounded-md
                          borderColor: "var(--button-border-color)",
                          boxShadow: "none",
                          "&:hover": {
                            borderColor: "var(--button-border-color)",
                          },
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                      }}
                      value={
                        roleOptions.find((r) => r.value === newStage.role) ||
                        null
                      }
                      onChange={async (option) => {
                        const selected = option?.value || "";
                        const selectedLabel = option?.label || "";

                        setNewStage((prev) => ({
                          ...prev,
                          role: selected,
                          role_name: selectedLabel,
                          users: [],
                        }));

                        if (selected) {
                          setRowLoad(selected, true);
                          const opts = await fetchUsersByRole(selected);
                          setRowLoad(selected, false);
                          setUsersOptionsByRole((prev) => ({
                            ...prev,
                            [selected]: opts,
                          }));
                        }
                      }}
                    />
                  </div>

                  {/* <div>
                    <label className="text-sm font-bold">Approval Timeline</label>
                    <select
                      className="border rounded-md w-full px-3 py-2 mt-2"
                      value={newStage.timeline}
                      onChange={(e) =>
                        setNewStage((prev) => ({
                          ...prev,
                          timeline: e.target.value,
                        }))
                      }
                    >
                      <option value="">Select timeline</option>
                      {TIMELINES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                    <div className="pt-1 flex items-center gap-2">
                      <Info
                        size={20}
                        fill="var(--description-color)"
                        stroke="white"
                      />
                      <span className="text-xs">
                        Define the maximum time allowed for this approval.
                      </span>
                    </div>
                  </div> */}
                  <div>
                    <label className="text-sm font-bold">Approval Timeline</label>
                    {(() => {
                      const selectedTimelineOption =
                        TIMELINES.find((t) => t.value === newStage.timeline) || null;
                      return (
                        <Select
                          className="mt-2"
                          classNamePrefix="timeline-select"
                          options={TIMELINES}
                          placeholder="Select timeline"
                          isSearchable={true}
                          maxMenuHeight={5 * 40}
                          components={{ IndicatorSeparator: () => null }}
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: "40px",
                              borderRadius: "0.375rem",
                              borderColor: "var(--button-border-color)",
                              boxShadow: "none",
                              "&:hover": {
                                borderColor: "var(--button-border-color)",
                              },
                            }),
                            menu: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                          }}
                          value={selectedTimelineOption}
                          onChange={(option) => {
                            const selected = option?.value || "";
                            setNewStage((prev) => ({
                              ...prev,
                              timeline: selected,
                            }));
                          }}
                        />
                      );
                    })()}
                    <div className="pt-1 flex items-center gap-2">
                      <Info
                        size={20}
                        fill="var(--description-color)"
                        stroke="white"
                      />
                      <span className="text-xs">
                        Define the maximum time allowed for this approval.
                      </span>
                    </div>
                  </div>

                </div>

                <div className="mb-4 flex flex-col gap-2">
                  <label className="text-sm font-bold">Users</label>
                  <MultiSelectChips
                    value={newStage.users.map((u) =>
                      typeof u === "string" ? u : u.value
                    )}
                    onChange={(val) => {
                      const selectedObjs = (val || []).map((v) => {
                        const obj =
                          (usersOptionsByRole[newStage.role] || []).find(
                            (o) => o.value === v
                          ) || { value: v, label: v };
                        return obj;
                      });
                      setNewStage((prev) => ({ ...prev, users: selectedObjs }));
                    }}
                    options={usersOptionsByRole[newStage.role] || []}
                    placeholder={
                      rowLoading[newStage.role]
                        ? "Loading users"
                        : "Search and select users"
                    }
                    disabled={!!rowLoading[newStage.role]}
                  />
                </div>

                <div className="mb-4">
                  <label className="text-sm font-bold">Actions</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 mt-3">
                    {Object.entries(newStage.actions).map(([key, val]) => {
                      const actionDescriptions = {
                        approve: "Allow this stage to approve.",
                        reject: "Permit rejection with remarks.",
                        rollback: "Send document back to previous stage.",
                        edit: "Allow modification before approval.",
                      };

                      return (
                        <label
                          key={key}
                          className="flex flex-col items-start gap-1 cursor-pointer"
                        >
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={val}
                              style={{ accentColor: "var(--primary)" }}
                              onChange={() =>
                                setNewStage((prev) => ({
                                  ...prev,
                                  actions: {
                                    ...prev.actions,
                                    [key]: !val,
                                  },
                                }))
                              }
                            />
                            <span className="capitalize text-sm font-medium">
                              {key}
                            </span>
                          </div>
                          <span className="text-xs text-[var(--description-color)] ml-6">
                            {actionDescriptions[key]}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-5">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 border rounded-md text-sm hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAddOrUpdateStage(push, replace)}
                    className="px-4 py-2 rounded-md text-sm bg-primary text-white hover:opacity-95 transition"
                  >
                    {editIndex !== null ? "Update Stage" : "Add Stage"}
                  </button>
                </div>
              </Dialog.Panel>
            </div>
          </Dialog>
        </div>
      )}
    />
  );
};

export default ApprovalStages;
